import { useState, useRef, useEffect } from "react";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ChevronRight,
  ChevronLeft,
  Send,
  RotateCcw,
  MessageSquare,
} from "lucide-react";
import { api, ChatLimitError, type ChatMessage, type ChatContext } from "../lib/api";

interface ChatPanelProps {
  open: boolean;
  onToggle: () => void;
}

export function ChatPanel({ open, onToggle }: ChatPanelProps) {
  const { videoId } = useParams<{ videoId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const onComments = location.pathname.startsWith("/youtube/comments");

  // Build the chat context from the current route. Video page → `video`;
  // the comments dashboard → `comments` (carrying its URL-driven filters);
  // everything else → `channel`.
  const context: ChatContext = videoId
    ? { type: "video", videoId }
    : onComments
    ? {
        type: "comments",
        // v8 multi-facet context (Phase 8 #9.6) — reads the dashboard's URL
        // contract (?sentiment=&gtag=&topic=&safety=&videoId=) so the agent
        // grounds on exactly the composed view the user is looking at.
        filters: {
          sentiment:
            (searchParams.get("sentiment") as
              | "supportive"
              | "unsupportive"
              | "neutral"
              | null) || undefined,
          generalTag:
            (searchParams.get("gtag") as
              | "question"
              | "challenge"
              | "misunderstanding"
              | "personal_story"
              | "content_request"
              | null) || undefined,
          topicTag: searchParams.get("topic") || undefined,
          safetyTag:
            (searchParams.get("safety") as
              | "spam_bot"
              | "threat_or_abuse"
              | null) || undefined,
          videoId: searchParams.get("videoId") || undefined,
        },
      }
    : { type: "channel" };

  // Reset key: a new conversation starts when the *scope* changes (channel ↔
  // comments ↔ a specific video). Tweaking dashboard filters does NOT reset —
  // current filters ride along on every turn via the context envelope.
  const contextKey =
    context.type === "video"
      ? `video:${context.videoId}`
      : context.type;

  const { data: video } = useQuery({
    queryKey: ["video", videoId],
    queryFn: () => api.video(videoId!),
    enabled: !!videoId,
  });

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  // Drop the conversation when the scope changes so answers stay grounded.
  useEffect(() => {
    setMessages([]);
    setConversationId(undefined);
  }, [contextKey]);

  const contextLabel =
    context.type === "video" && video
      ? `Ask about: ${video.title || videoId}`
      : context.type === "video"
      ? "Ask about: this video"
      : context.type === "comments"
      ? "Ask about these comments"
      : "Ask about the channel";

  const examples =
    context.type === "video"
      ? [
          "What's the strongest counter-argument here?",
          "Are there safety flags I missed?",
        ]
      : context.type === "comments"
      ? [
          "Summarise what people are saying in this view.",
          "Has anyone asked about housing reform?",
        ]
      : [
          "Which video had the most pushback this month?",
          "What's the most common content request right now?",
        ];

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const res = await api.chat({
        message: text,
        conversationId,
        context,
      });
      if (res.conversationId) setConversationId(res.conversationId);
      setMessages([...next, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages([
        ...next,
        {
          role: "assistant",
          content:
            e instanceof ChatLimitError
              ? e.message
              : `Couldn't reach the chat backend (${
                  e instanceof Error ? e.message : "unknown"
                }).`,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/*
        Desktop collapsed rail — visible only on md+ when chat is closed.
        On mobile the chat is opened from the top-bar button, so the rail
        isn't needed.
      */}
      {!open && (
        <button
          onClick={onToggle}
          className="hidden w-8 flex-col items-center gap-3 border-l border-rule bg-surface py-3 text-ink-muted hover:text-ink md:flex"
          aria-label="Open chat"
          title="Open chat"
        >
          <ChevronLeft size={14} />
          <MessageSquare size={14} />
        </button>
      )}

      {/* Mobile backdrop when drawer is open. */}
      {open && (
        <div
          className="fixed inset-0 top-14 z-20 bg-black/40 md:hidden"
          onClick={onToggle}
          aria-hidden
        />
      )}

      {/*
        Open panel: drawer-style on mobile (slides in from right, fixed
        overlay), in-flow column on md+. The same content tree renders
        both — only the wrapper positioning differs.
      */}
      {open && (
        <aside
          className="
            fixed top-14 bottom-0 right-0 z-30 flex w-[88vw] max-w-md flex-col border-l border-rule bg-surface text-ink
            md:static md:z-auto md:h-full md:w-90 md:max-w-none
          "
        >
          <div className="flex items-center justify-between gap-2 border-b border-rule px-4 py-3">
            <div className="min-w-0">
              <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                Chat
              </div>
              <div
                className="truncate text-sm font-medium"
                title={contextLabel}
              >
                {contextLabel}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={() => {
                    setMessages([]);
                    setConversationId(undefined);
                  }}
                  className="rounded p-1 text-ink-muted hover:bg-black/5 hover:text-ink"
                  aria-label="Clear conversation"
                  title="Clear conversation"
                >
                  <RotateCcw size={14} />
                </button>
              )}
              <button
                onClick={onToggle}
                className="rounded p-1 text-ink-muted hover:bg-black/5 hover:text-ink"
                aria-label="Close chat"
                title="Close chat"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {messages.length === 0 ? (
              <div className="text-sm text-ink-muted">
                <p className="mb-3">
                  Ask anything about{" "}
                  {context.type === "video"
                    ? "this video"
                    : context.type === "comments"
                    ? "these comments"
                    : "the channel"}
                  . Examples:
                </p>
                <ul className="space-y-1.5">
                  {examples.map((ex) => (
                    <li key={ex}>
                      <button
                        onClick={() => setInput(ex)}
                        className="text-left text-brand-indigo hover:underline"
                      >
                        {ex}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m, i) =>
                  m.role === "user" ? (
                    <div
                      key={i}
                      className="ml-6 whitespace-pre-wrap rounded bg-black/5 px-3 py-2 text-sm leading-relaxed text-ink"
                    >
                      {m.content}
                    </div>
                  ) : (
                    <div key={i} className="chat-md mr-6">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {m.content}
                      </ReactMarkdown>
                    </div>
                  ),
                )}
                {sending && (
                  <div className="mr-6 text-sm text-ink-muted">
                    <span className="inline-block animate-pulse">
                      Thinking…
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t border-rule p-3">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={2}
                placeholder="Ask…"
                className="flex-1 resize-none rounded border border-rule bg-paper px-2 py-1.5 text-sm text-ink placeholder:text-ink-muted focus:border-brand-indigo focus:bg-surface focus:outline-none"
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending}
                className="self-end rounded bg-brand-indigo p-2 text-white transition-opacity hover:opacity-90 disabled:opacity-30"
                aria-label="Send"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </aside>
      )}
    </>
  );
}
